/**
 * Business Type Service
 * 
 * Service for fetching and managing business claim types from the backend.
 * Integrates with the backend's business-relevant claim types API endpoint.
 * 
 * Evidence: Backend provides /api/v1/group/defaults/getClaimsType with business filtering
 * API Documentation: /api/v1/group/defaults/getClaimsType?businessRelevantOnly=true returns 8 business types
 */

import { apiClient } from '../utils/apiClient';
import { BUSINESS_TYPE_FIELD_MAPPINGS } from '../constants/businessTypeFieldMappings';

/**
 * Fetch business claim types from the backend
 * @returns {Promise<Array>} Array of business claim type objects from backend
 * @throws {Error} If API call fails
 */
const fetchBusinessClaimTypes = async () => {
  try {
    console.log('🔍 BusinessTypeService: Starting fetchBusinessClaimTypes...');
    console.log('🌐 BusinessTypeService: Calling API endpoint: defaults/getClaimsType?businessRelevantOnly=true');
    
    const response = await apiClient.get('defaults/getClaimsType?businessRelevantOnly=true');
    
    console.log('📡 BusinessTypeService: Raw API response received:', {
      status: response.status,
      headers: response.headers,
      hasData: !!response.data,
      dataType: typeof response.data,
      dataConstructor: response.data?.constructor?.name,
      isArray: Array.isArray(response.data),
      dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A'
    });
    
    console.log('📋 BusinessTypeService: Full response.data structure:', response.data);
    
    // Check if response.data is the array we need
    if (Array.isArray(response.data)) {
      console.log('✅ BusinessTypeService: response.data is array, length:', response.data.length);
      console.log('🎯 BusinessTypeService: First item structure:', response.data[0]);
      return response.data;
    }
    
    // Check if it's wrapped in a data property
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      console.log('✅ BusinessTypeService: Found array in response.data.data, length:', response.data.data.length);
      console.log('🎯 BusinessTypeService: First item structure:', response.data.data[0]);
      return response.data.data;
    }
    
    // Check if it's double-wrapped
    if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
      console.log('✅ BusinessTypeService: Found array in response.data.data.data (double-wrapped), length:', response.data.data.data.length);
      console.log('🎯 BusinessTypeService: First item structure:', response.data.data.data[0]);
      return response.data.data.data;
    }
    
    // If we get here, the structure is unexpected
    console.error('❌ BusinessTypeService: Unexpected response structure - no array found');
    console.error('🔍 BusinessTypeService: Response structure debug:', {
      hasData: !!response.data,
      dataType: typeof response.data,
      dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A',
      fullData: response.data
    });
    
    // Return empty array as fallback
    console.warn('⚠️ BusinessTypeService: Returning empty array as fallback');
    return [];
    
  } catch (error) {
    console.error('❌ BusinessTypeService: Error in fetchBusinessClaimTypes:', {
      message: error.message,
      status: error.status,
      response: error.response,
      stack: error.stack
    });
    
    // Return empty array on error
    console.warn('⚠️ BusinessTypeService: Returning empty array due to error');
    return [];
  }
};

/**
 * Get field requirements for a specific business type
 * @param {string} businessType - Business claim type code (DTH, 010, 012, etc.)
 * @returns {Array} Array of required field names for the business type
 */
const getFieldRequirements = (businessType) => {
  return BUSINESS_TYPE_FIELD_MAPPINGS[businessType] || [];
};

/**
 * Get business type metadata by code
 * @param {string} typeCode - Business claim type code
 * @returns {Promise<Object>} Business type metadata
 * @throws {Error} If API call fails
 */
const getBusinessTypeMetadata = async (typeCode) => {
  // TODO: Check if backend has individual claim type endpoint
  // For now, fetch all types and filter
  const allTypes = await fetchBusinessClaimTypes();
  return allTypes.find(type => type.typeCode === typeCode || type.value === typeCode);
};

/**
 * Validate business type fields against backend business rules
 * @param {Object} data - Claim data to validate
 * @returns {Promise<Object>} Validation result from business rules engine
 * @throws {Error} If API call fails
 */
const validateBusinessTypeFields = async (data) => {
  const response = await apiClient.post('claims/validate-rules', data);
  return response.data;
};

const businessTypeService = {
  fetchBusinessClaimTypes,
  getFieldRequirements,
  getBusinessTypeMetadata,
  validateBusinessTypeFields
};

export default businessTypeService;